import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, UsersRound, MailWarning, CheckCircle2 } from "lucide-react";

const CRIMSON = "var(--brand)";

const STATUS_LABEL = {
  created: "Login created + welcome sent",
  linked_existing: "Linked existing user + welcome sent",
  already_linked: "Skipped — already has a login",
  no_email: "Skipped — no email on file",
  not_found: "Skipped — staff not found",
  failed: "Failed",
};
const STATUS_TONE = {
  created: "text-emerald-700", linked_existing: "text-emerald-700",
  already_linked: "text-slate-400", no_email: "text-amber-600",
  not_found: "text-slate-400", failed: "text-red-600",
};

export function BulkLoginsDialog({ open, onOpenChange, onDone }) {
  const [data, setData] = useState(null);   // { missing, count, without_email }
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) { setData(null); setResults(null); setBusy(false); return; }
    api.hrLoginsMissing().then(setData).catch((e) => toast.error(e.response?.data?.detail || "Couldn't load staff."));
  }, [open]);

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.hrBulkCreateLogins({ roles: ["Support Worker"], send_welcome: true });
      setResults(res);
      toast.success(`${res.created} login${res.created === 1 ? "" : "s"} created · ${res.welcomed} welcome email${res.welcomed === 1 ? "" : "s"} sent`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk create failed."); }
    finally { setBusy(false); }
  };

  const eligible = data ? data.count - data.without_email : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bulk-logins-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><UsersRound className="h-4 w-4" style={{ color: CRIMSON }} />Bulk create staff logins</DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="py-8 text-center text-slate-400 text-sm"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading…</div>
        ) : results ? (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="bulk-logins-results">
            <p className="text-sm text-slate-600"><strong className="text-slate-900">{results.created}</strong> created, <strong className="text-slate-900">{results.welcomed}</strong> welcome emails sent, {results.total - results.created} skipped/failed.</p>
            {results.results.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs border-b border-slate-100 py-1.5">
                <span className="text-slate-700 truncate mr-2">{r.display_name || r.name}{r.email ? <span className="text-slate-400"> · {r.email}</span> : null}</span>
                <span className={`shrink-0 font-medium ${STATUS_TONE[r.status] || "text-slate-500"}`}>{STATUS_LABEL[r.status] || r.status}{r.reason ? ` (${r.reason})` : ""}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This will create a login for every <strong>active</strong> staff member who doesn't have one yet, default them to the <strong>Support Worker</strong> role, and email each a welcome / set-password link.
            </p>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="flex justify-between py-0.5"><span className="text-slate-500">Staff without a login</span><span className="font-semibold text-slate-900" data-testid="bulk-missing-count">{data.count}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-slate-500">Will be onboarded</span><span className="font-semibold text-emerald-700" data-testid="bulk-eligible-count">{eligible}</span></div>
              {data.without_email > 0 && (
                <div className="flex justify-between py-0.5 text-amber-600"><span className="flex items-center gap-1"><MailWarning className="h-3.5 w-3.5" />No email on file (skipped)</span><span className="font-semibold" data-testid="bulk-noemail-count">{data.without_email}</span></div>
              )}
            </div>
            {data.count > 0 && (
              <div className="max-h-[30vh] overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-100">
                {data.missing.map((m, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                    <span className="text-slate-700 truncate mr-2">{m.display_name}</span>
                    {m.has_email
                      ? <span className="text-slate-400 shrink-0">{m.email}</span>
                      : <span className="text-amber-600 shrink-0 flex items-center gap-1"><MailWarning className="h-3 w-3" />no email</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {results ? (
            <Button size="sm" onClick={() => onOpenChange(false)} data-testid="bulk-logins-close">Done</Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button size="sm" style={{ backgroundColor: CRIMSON }} disabled={busy || !data || eligible === 0} onClick={run} data-testid="bulk-logins-run">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}Create {eligible} login{eligible === 1 ? "" : "s"} &amp; send welcome
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
